import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  gstinCheckDigit,
  normalizeMobile,
  panHolderType,
  sameState,
  stateOfGstin,
} from "./companyChecks";

// Company and contact details a bidder participates with. Asked every time a
// bidder joins an auction (pre-filled from their last one) and saved with
// their acceptance of that auction's terms.

export const EMPTY_COMPANY = {
  companyName: "",
  gstin: "",
  pan: "",
  pinCode: "",
  city: "",
  state: "",
  contactName: "",
  designation: "",
  contactPhone: "",
  contactEmail: "",
};

const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PIN_PATTERN = /^[1-9][0-9]{5}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[A-Za-z]{2,}$/;

const REQUIRED = {
  companyName: "Enter the company name",
  pan: "Enter the PAN",
  pinCode: "Enter the PIN code",
  city: "Enter the city",
  state: "Enter the state",
  contactName: "Enter the name of the person bidding",
  designation: "Enter the designation",
  contactPhone: "Enter a phone number",
  contactEmail: "Enter an email",
};

// same rules as the server; returns { field: message } for each problem
export const validateCompany = (company) => {
  const errors = {};
  Object.entries(REQUIRED).forEach(([field, message]) => {
    if (!String(company[field] || "").trim()) errors[field] = message;
  });
  if (company.gstin && !GSTIN_PATTERN.test(company.gstin)) {
    errors.gstin = "GSTIN is not valid (15 characters, e.g. 27AAACR5055K1Z7)";
  } else if (
    company.gstin &&
    gstinCheckDigit(company.gstin) !== company.gstin[14]
  ) {
    errors.gstin = "GSTIN is not valid. Check the number for typing mistakes.";
  }
  if (company.pan && !PAN_PATTERN.test(company.pan)) {
    errors.pan = "PAN is not valid (e.g. AAACR5055K)";
  } else if (company.pan && !panHolderType(company.pan)) {
    errors.pan = "PAN is not valid: its 4th letter isn't a PAN holder type";
  } else if (
    company.pan &&
    GSTIN_PATTERN.test(company.gstin || "") &&
    company.gstin.slice(2, 12) !== company.pan
  ) {
    errors.pan = "PAN does not match the GSTIN";
  }
  if (company.pinCode && !PIN_PATTERN.test(company.pinCode)) {
    errors.pinCode = "PIN code must be 6 digits";
  }
  if (company.contactPhone && !normalizeMobile(company.contactPhone)) {
    errors.contactPhone = "Enter a valid 10-digit mobile number";
  }
  if (company.contactEmail && !EMAIL_PATTERN.test(company.contactEmail)) {
    errors.contactEmail = "Email is not valid";
  }
  return errors;
};

// City and state for an Indian PIN code from India Post's public lookup.
// Returns null when the lookup fails, so the bidder types them in instead.
const lookupPinCode = async (pinCode) => {
  try {
    const response = await fetch(
      `https://api.postalpincode.in/pincode/${pinCode}`,
    );
    const [result] = await response.json();
    const [office] = result?.PostOffice || [];
    if (result?.Status !== "Success" || !office) return null;
    return { city: office.District, state: office.State };
  } catch (error) {
    return null;
  }
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ color: "#2D55EB", fontWeight: 600, mt: 2, mb: 2 }}
  >
    {children}
  </Typography>
);

// one row of fields; stacks into a single column on phones
const FieldRow = ({ columns, children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, 1fr)` },
      columnGap: 2,
      rowGap: 1,
      mb: 1.5,
    }}
  >
    {children}
  </Box>
);

const BidderCompanyForm = ({ value, onChange, errors, onFieldEdit }) => {
  const [pinStatus, setPinStatus] = useState(""); // "", "loading", "found", "failed"
  const latestPin = useRef("");

  const set = (changes) => {
    onChange({ ...value, ...changes });
    Object.keys(changes).forEach((field) => onFieldEdit?.(field));
  };

  const handleGstin = (event) => {
    const gstin = event.target.value.toUpperCase().replace(/\s/g, "");
    if (!GSTIN_PATTERN.test(gstin)) {
      set({ gstin });
      return;
    }
    // the PAN is part of the GSTIN and its first two digits are the state
    // code, so fill those in from there
    const gstinState = stateOfGstin(gstin);
    set({
      gstin,
      pan: gstin.slice(2, 12),
      ...(gstinState && !value.state ? { state: gstinState } : {}),
    });
  };

  useEffect(() => {
    const pinCode = value.pinCode;
    latestPin.current = pinCode;
    if (!PIN_PATTERN.test(pinCode || "")) {
      setPinStatus("");
      return;
    }
    setPinStatus("loading");
    lookupPinCode(pinCode).then((place) => {
      if (latestPin.current !== pinCode) return; // PIN changed meanwhile
      if (!place) {
        setPinStatus("failed");
        return;
      }
      setPinStatus("found");
      onChange((current) => ({ ...current, ...place }));
      onFieldEdit?.("city");
      onFieldEdit?.("state");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.pinCode]);

  const panFromGstin = GSTIN_PATTERN.test(value.gstin || "");
  const panType = PAN_PATTERN.test(value.pan || "") && panHolderType(value.pan);
  // a GSTIN registered in another state than the address is allowed (a
  // branch can bid), but worth a second look
  const gstinState = panFromGstin && stateOfGstin(value.gstin);
  const stateHint =
    gstinState && value.state && !sameState(gstinState, value.state)
      ? `GSTIN is registered in ${gstinState}`
      : " ";

  // an error replaces the field's hint until the field is edited
  const field = (name, label, { helperText, ...props } = {}) => (
    <TextField
      name={name}
      label={label}
      value={value[name] || ""}
      onChange={(event) => set({ [name]: event.target.value })}
      error={Boolean(errors[name])}
      helperText={errors[name] || helperText || " "}
      required={Boolean(REQUIRED[name])}
      size="small"
      fullWidth
      {...props}
    />
  );

  const pinHelper = {
    loading: "Looking up city and state…",
    found: "City and state filled from the PIN code",
    failed: "Couldn't look up this PIN code. Enter the city and state.",
  }[pinStatus];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enter the company you're bidding for in this auction. These details are
        shared with the auctioneer and saved with your acceptance of the terms.
      </Typography>

      <SectionTitle>Company</SectionTitle>
      <FieldRow columns={1}>
        {field("companyName", "Company name", { autoFocus: true })}
      </FieldRow>
      <FieldRow columns={2}>
        {field("gstin", "GSTIN (optional)", {
          onChange: handleGstin,
          inputProps: { maxLength: 15 },
          helperText: "PAN is filled from the GSTIN",
        })}
        {field("pan", "PAN", {
          onChange: (event) =>
            set({
              pan: event.target.value.toUpperCase().replace(/\s/g, ""),
            }),
          inputProps: { maxLength: 10, readOnly: panFromGstin },
          helperText: [panFromGstin && "From the GSTIN", panType]
            .filter(Boolean)
            .join(" · "),
        })}
      </FieldRow>
      <FieldRow columns={3}>
        {field("pinCode", "PIN code", {
          onChange: (event) =>
            set({ pinCode: event.target.value.replace(/\D/g, "") }),
          inputProps: { maxLength: 6, inputMode: "numeric" },
          helperText: pinHelper,
          InputProps:
            pinStatus === "loading"
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ),
                }
              : undefined,
        })}
        {field("city", "City")}
        {field("state", "State", {
          helperText: stateHint,
          FormHelperTextProps: { sx: { color: "warning.dark" } },
        })}
      </FieldRow>

      <SectionTitle>Person bidding</SectionTitle>
      <FieldRow columns={2}>
        {field("contactName", "Name")}
        {field("designation", "Designation")}
      </FieldRow>
      <FieldRow columns={2}>
        {field("contactPhone", "Phone", {
          inputProps: { maxLength: 14, inputMode: "tel" },
        })}
        {field("contactEmail", "Email", { type: "email" })}
      </FieldRow>
    </Box>
  );
};

export default BidderCompanyForm;
